import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCategoryByIdThunk } from "../../../store/thunks/categoryThunks";
import CategoryPricingManager from "../CategoryPricingManager"; // Import the pricing manager
import Modal from "../../../components/common/Modal";
import Badge from "../../../components/common/Badge";
import Button from "../../../components/common/Button";
import {
  InformationCircleIcon,
  CollectionIcon,
  ChartBarIcon,
  LocationMarkerIcon,
  TagIcon,
  CalendarIcon,
  ShoppingBagIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  CurrencyDollarIcon, // Add this icon for pricing
} from "@heroicons/react/outline";

const CategoryDetailView = ({ categoryId, isOpen, onClose, onEdit }) => {
  const dispatch = useDispatch();
  const { selectedCategory, loading } = useSelector((state) => state.category);
  const { branches } = useSelector((state) => state.branch); // Assuming you have branches in state
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    if (isOpen && categoryId) {
      dispatch(
        getCategoryByIdThunk({
          categoryId,
          options: {
            includeProducts: true,
            includeChildren: true,
            includeAncestors: true,
          },
        })
      );
    }
  }, [dispatch, categoryId, isOpen]);

  if (!isOpen) return null;

  const tabs = [
    { id: "overview", label: "Overview", icon: InformationCircleIcon },
    { id: "classification", label: "Classification", icon: CollectionIcon },
    { id: "statistics", label: "Statistics", icon: ChartBarIcon },
    { id: "pricing", label: "Pricing", icon: CurrencyDollarIcon }, // NEW TAB
    { id: "availability", label: "Availability", icon: LocationMarkerIcon },
    { id: "seo", label: "SEO", icon: GlobeAltIcon },
  ];

  const formatDate = (date) => {
    return date
      ? new Date(date).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "N/A";
  };

  const renderBreadcrumb = () => {
    if (
      !selectedCategory?.ancestors ||
      selectedCategory.ancestors.length === 0
    ) {
      return <span className="text-gray-500">Root Category</span>;
    }

    return (
      <div className="flex items-center gap-2 text-sm">
        {selectedCategory.ancestors.map((ancestor, index) => (
          <React.Fragment key={ancestor._id}>
            <span className="text-gray-600">{ancestor.name}</span>
            {index < selectedCategory.ancestors.length - 1 && (
              <span className="text-gray-400">/</span>
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      closeModal={onClose}
      isFullScreen={true}
      title="Category Details"
      size="xlarge"
    >
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : selectedCategory ? (
        <div className="space-y-4">
          {/* Header */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {selectedCategory.name}
                </h2>
                {renderBreadcrumb()}
                <div className="flex items-center gap-4 mt-3 text-sm text-gray-600">
                  <span className="font-mono">
                    ID:{" "}
                    {selectedCategory.categoryId ||
                      selectedCategory._id?.slice(-6)}
                  </span>
                  <span>•</span>
                  <span>Level: {selectedCategory.parentLevel || 0}</span>
                  {selectedCategory.slug && (
                    <>
                      <span>•</span>
                      <span>Slug: {selectedCategory.slug}</span>
                    </>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                {selectedCategory.display?.isActive ? (
                  <Badge variant="success">Active</Badge>
                ) : (
                  <Badge variant="danger">Inactive</Badge>
                )}
                {selectedCategory.display?.isFeatured && (
                  <Badge variant="primary">Featured</Badge>
                )}
                {selectedCategory.pricingConfig?.branchSpecificRules?.length >
                  0 && (
                  <Badge variant="info">
                    {selectedCategory.pricingConfig.branchSpecificRules.length}{" "}
                    Pricing Rules
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b">
            <div className="flex space-x-4 overflow-x-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2 font-medium transition-colors border-b-2 whitespace-nowrap ${
                      activeTab === tab.id
                        ? "text-primary border-primary"
                        : "text-gray-500 border-transparent hover:text-gray-700"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab Content */}
          <div className="min-h-[300px]">
            {/* Overview Tab - unchanged */}
            {activeTab === "overview" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-800 mb-3">
                    Basic Information
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-gray-500">
                        Description
                      </label>
                      <p className="text-gray-800">
                        {selectedCategory.description ||
                          "No description provided"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">
                        Display Order
                      </label>
                      <p className="text-gray-800">
                        {selectedCategory.order || 0}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">Tags</label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {selectedCategory.tags?.length > 0 ? (
                          selectedCategory.tags.map((tag, idx) => (
                            <Badge
                              key={idx}
                              variant="light"
                              className="text-xs"
                            >
                              <TagIcon className="w-3 h-3 mr-1" />
                              {tag}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-gray-500 text-sm">No tags</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-800 mb-3">
                    Display Settings
                  </h3>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between py-2 border-b">
                      <span className="text-sm text-gray-600">
                        Show in Menu
                      </span>
                      <Badge
                        variant={
                          selectedCategory.display?.showInMenu
                            ? "success"
                            : "secondary"
                        }
                      >
                        {selectedCategory.display?.showInMenu ? "Yes" : "No"}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b">
                      <span className="text-sm text-gray-600">
                        Show in Mobile
                      </span>
                      <Badge
                        variant={
                          selectedCategory.display?.showInMobile
                            ? "success"
                            : "secondary"
                        }
                      >
                        {selectedCategory.display?.showInMobile ? "Yes" : "No"}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b">
                      <span className="text-sm text-gray-600">
                        Show on Homepage
                      </span>
                      <Badge
                        variant={
                          selectedCategory.display?.showInHomepage
                            ? "success"
                            : "secondary"
                        }
                      >
                        {selectedCategory.display?.showInHomepage
                          ? "Yes"
                          : "No"}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b">
                      <span className="text-sm text-gray-600">
                        Display Mode
                      </span>
                      <span className="text-sm font-medium">
                        {selectedCategory.display?.displayMode || "GRID"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Children Categories */}
                {selectedCategory.children &&
                  selectedCategory.children.length > 0 && (
                    <div className="md:col-span-2">
                      <h3 className="font-semibold text-gray-800 mb-3">
                        Child Categories ({selectedCategory.children.length})
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {selectedCategory.children.map((child) => (
                          <div
                            key={child._id}
                            className="bg-gray-50 p-3 rounded-lg"
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-gray-800">
                                {child.name}
                              </span>
                              {child.display?.isActive ? (
                                <Badge variant="success" className="text-xs">
                                  Active
                                </Badge>
                              ) : (
                                <Badge variant="secondary" className="text-xs">
                                  Inactive
                                </Badge>
                              )}
                            </div>
                            {child.productCount !== undefined && (
                              <div className="text-sm text-gray-500 mt-1">
                                {child.productCount} products
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            )}

            {/* Classification Tab - unchanged */}
            {activeTab === "classification" && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm text-gray-500">
                      Target Gender
                    </label>
                    <p className="text-gray-800 font-medium">
                      {selectedCategory.classification?.targetGender || "ALL"}
                    </p>
                  </div>

                  <div>
                    <label className="text-sm text-gray-500">
                      Product Types
                    </label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {selectedCategory.classification?.productTypes?.length >
                      0 ? (
                        selectedCategory.classification.productTypes.map(
                          (type, idx) => (
                            <Badge key={idx} variant="light">
                              {type}
                            </Badge>
                          )
                        )
                      ) : (
                        <span className="text-gray-500">None specified</span>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="text-sm text-gray-500">Occasions</label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {selectedCategory.classification?.occasions?.length >
                      0 ? (
                        selectedCategory.classification.occasions.map(
                          (occasion, idx) => (
                            <Badge key={idx} variant="light">
                              {occasion}
                            </Badge>
                          )
                        )
                      ) : (
                        <span className="text-gray-500">None specified</span>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="text-sm text-gray-500">Collections</label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {selectedCategory.classification?.collections?.length >
                      0 ? (
                        selectedCategory.classification.collections.map(
                          (collection, idx) => (
                            <Badge key={idx} variant="light">
                              {collection}
                            </Badge>
                          )
                        )
                      ) : (
                        <span className="text-gray-500">None specified</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Statistics Tab - unchanged */}
            {activeTab === "statistics" && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center gap-2 text-gray-500 mb-1">
                      <ShoppingBagIcon className="w-4 h-4" />
                      <span className="text-sm">Total Products</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">
                      {selectedCategory.productCount ||
                        selectedCategory.stats?.totalProducts ||
                        0}
                    </p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center gap-2 text-gray-500 mb-1">
                      <span className="text-sm">Active Products</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">
                      {selectedCategory.stats?.activeProducts || 0}
                    </p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center gap-2 text-gray-500 mb-1">
                      <span className="text-sm">In Stock</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">
                      {selectedCategory.stats?.inStockProducts || 0}
                    </p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center gap-2 text-gray-500 mb-1">
                      <span className="text-sm">Total Views</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">
                      {selectedCategory.stats?.totalViews || 0}
                    </p>
                  </div>
                </div>

                <div className="mt-6">
                  <label className="text-sm text-gray-500">Last Updated</label>
                  <p className="text-gray-800">
                    {formatDate(selectedCategory.stats?.lastUpdated)}
                  </p>
                </div>

                {/* Sample Products */}
                {selectedCategory.products &&
                  selectedCategory.products.length > 0 && (
                    <div className="mt-6">
                      <h3 className="font-semibold text-gray-800 mb-3">
                        Sample Products ({selectedCategory.products.length})
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Product ID
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Name
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                SKU
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Type
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                Stock
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {selectedCategory.products
                              .slice(0, 5)
                              .map((product) => (
                                <tr key={product._id}>
                                  <td className="px-4 py-2 text-sm text-gray-900">
                                    {product.productId}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-900">
                                    {product.name}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-900">
                                    {product.sku || "N/A"}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-900">
                                    {product.type}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-900">
                                    {product.stock?.availableQuantity || 0}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
              </div>
            )}

            {/* PRICING TAB - NEW */}
            {activeTab === "pricing" && (
              <div className="space-y-4">
                <CategoryPricingManager
                  category={selectedCategory}
                  branches={branches || []} // You'll need to pass branches from your state/props
                />
              </div>
            )}

            {/* Availability Tab - unchanged */}
            {activeTab === "availability" && (
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-500">
                    Availability Status
                  </label>
                  <p className="text-gray-800 font-medium mt-1">
                    {selectedCategory.availability?.allBranches ? (
                      <Badge variant="success" className="text-sm">
                        Available at All Branches
                      </Badge>
                    ) : (
                      <Badge variant="warning" className="text-sm">
                        Limited Availability
                      </Badge>
                    )}
                  </p>
                </div>

                {selectedCategory.availability?.specificBranches?.length >
                  0 && (
                  <div>
                    <label className="text-sm text-gray-500">
                      Specific Branches
                    </label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {selectedCategory.availability.specificBranches.map(
                        (branch, idx) => (
                          <Badge key={idx} variant="light">
                            <LocationMarkerIcon className="w-3 h-3 mr-1" />
                            {branch}
                          </Badge>
                        )
                      )}
                    </div>
                  </div>
                )}

                {selectedCategory.availability?.specificStores?.length > 0 && (
                  <div>
                    <label className="text-sm text-gray-500">
                      Specific Stores
                    </label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {selectedCategory.availability.specificStores.map(
                        (store, idx) => (
                          <Badge key={idx} variant="light">
                            {store}
                          </Badge>
                        )
                      )}
                    </div>
                  </div>
                )}

                {selectedCategory.availability?.excludedBranches?.length >
                  0 && (
                  <div>
                    <label className="text-sm text-gray-500">
                      Excluded Branches
                    </label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {selectedCategory.availability.excludedBranches.map(
                        (branch, idx) => (
                          <Badge key={idx} variant="danger">
                            {branch}
                          </Badge>
                        )
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* SEO Tab - unchanged */}
            {activeTab === "seo" && (
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-500">Meta Title</label>
                  <p className="text-gray-800">
                    {selectedCategory.seo?.metaTitle || "Not set"}
                  </p>
                </div>

                <div>
                  <label className="text-sm text-gray-500">
                    Meta Description
                  </label>
                  <p className="text-gray-800">
                    {selectedCategory.seo?.metaDescription || "Not set"}
                  </p>
                </div>

                <div>
                  <label className="text-sm text-gray-500">Meta Keywords</label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {selectedCategory.seo?.metaKeywords?.length > 0 ? (
                      selectedCategory.seo.metaKeywords.map((keyword, idx) => (
                        <Badge key={idx} variant="light">
                          {keyword}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-gray-500">No keywords set</span>
                    )}
                  </div>
                </div>

                <div>
                  <label className="text-sm text-gray-500">Canonical URL</label>
                  <p className="text-gray-800">
                    {selectedCategory.seo?.canonicalUrl || "Not set"}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="flex justify-between items-center pt-4 border-t">
            <div className="text-sm text-gray-500">
              <CalendarIcon className="w-4 h-4 inline mr-1" />
              Created: {formatDate(selectedCategory.createdAt)}
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={onClose}>
                Close
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  onEdit(selectedCategory);
                  onClose();
                }}
              >
                Edit Category
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          No category data available
        </div>
      )}
    </Modal>
  );
};

export default CategoryDetailView;
